import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  FluentProvider, createDarkTheme, createLightTheme,
  type BrandVariants, type Theme,
} from '@fluentui/react-components';
/*
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * ➕ `GD-02` — BA BỘ CHỮ ĐÓNG GÓI THEO APP, KHÔNG TẢI TỪ GOOGLE.
 *
 * 🔴 Vì sao điều này không phải chuyện gu: app điều khiển chiếu sáng chạy trong mạng nội bộ
 *   của khách, và nhiều tủ nằm sau một đường truyền không ra được Internet. Một `@import` tới
 *   `fonts.googleapis.com` ở đó **không hỏng thấy được** — nó chỉ chậm 3 giây rồi rơi về phông
 *   mặc định, và cả giao diện lệch đi mà không ai biết vì sao.
 * ⛔ Cách chấm của `GD-02` đo đúng điều này: tab Network khi mở app **không có** một yêu cầu
 *   nào tới `fonts.googleapis.com`/`fonts.gstatic.com`.
 *
 * ⚠ Nhập đúng những nét DÙNG TỚI, không nhập cả họ: mỗi nét là một tệp `.woff2` thật đi vào
 *   gói. Playfair chỉ cần 600/700 (nó chỉ dùng cho tiêu đề và số to).
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
import '@fontsource/lexend/400.css';
import '@fontsource/lexend/500.css';
import '@fontsource/lexend/600.css';
import '@fontsource/lexend/700.css';
import '@fontsource/be-vietnam-pro/400.css';
import '@fontsource/be-vietnam-pro/500.css';
import '@fontsource/be-vietnam-pro/600.css';
import '@fontsource/be-vietnam-pro/700.css';
import '@fontsource/playfair-display/600.css';
import '@fontsource/playfair-display/700.css';
import App from './App';
import { AuthProvider } from './lib/auth';
import { GiaoDienProvider, useGiaoDien } from './lib/giaodien';
import { ToastProvider } from './components/ui';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Dữ liệu vận hành: đã có WebSocket đẩy tin nên không cần hỏi lại liên tục,
      // nhưng quay lại tab thì nên làm mới ngay.
      staleTime: 10_000,
      refetchOnWindowFocus: true,
      retry: (failCount, err: any) => {
        // Không thử lại khi server đã trả lời rõ ràng là sai quyền / không tìm thấy
        const s = err?.status;
        if (s === 401 || s === 403 || s === 404 || s === 400 || s === 409) return false;
        return failCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * DẢI THƯƠNG HIỆU — 16 bậc dựng quanh `#3b82f6` (`--nhan` của chế độ tối).
 *
 * ⛔ FLUENT ĐÒI ĐỦ 16 BẬC, không nhận một mã màu. `createDarkTheme(brand)` dùng chúng để dựng
 *   hàng chục token (nút chính, nút đang rê chuột, viền đang chọn, chữ trên nền thương hiệu…).
 *   Thiếu bậc nào thì token dựng từ bậc ấy ra `undefined` và thành phần ấy mất màu.
 *
 * ⚠ VÀ ĐÂY LÀ CHỖ HAI HỆ MÀU DỄ TRÔI KHỎI NHAU NHẤT: `thietke.css` có `--nhan` của riêng nó,
 *   Fluent có dải này của riêng nó. Chúng phải **gặp nhau ở `#3b82f6`** — bậc `80` dưới đây
 *   chính là màu ấy, và `colorBrandBackground` của Fluent lấy từ quanh đó. Lệch một bậc thì
 *   nút "Tạo tài khoản" xanh một kiểu còn mục menu đang chọn xanh một kiểu khác, trên cùng một
 *   màn hình.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
/*
 * ⚠ `GD-07A` 11/09/2026 — DẢI DỰNG LẠI QUANH `#2c8afd` (`--nhan` mới của bảng "Xanh đêm").
 *   Bậc `90` vẫn là chỗ hai hệ màu GẶP NHAU, và mục `[99]` đo đúng cặp ấy: lệch một bậc thì
 *   nút "Tạo tài khoản" xanh một kiểu còn mục menu đang chọn xanh một kiểu khác, cùng màn hình.
 * ⚠ Bậc `70` cố ý bằng `--nhan-dam` (#0a469d) — chân dải nút chính. Ba bậc `70/90/100` là
 *   `nhan-dam / nhan / nhan-sang`, tức là **cùng một thang** với ba token của `thietke.css`,
 *   không phải hai thang chạy song song.
 */
const NHAN_SDKD: BrandVariants = {
  10: '#02070f',
  20: '#0a1b31',
  30: '#0c2749',
  40: '#0b3260',
  50: '#0a3c7b',
  60: '#0a4190',
  70: '#0a469d',   /* ĐÚNG `--nhan-dam` — chân dải nút chính */
  80: '#1a6cd4',
  90: '#2c8afd',   /* ĐÚNG `--nhan` của chế độ tối */
  100: '#39a3fc',  /* ĐÚNG `--nhan-sang` — đỉnh dải nút chính */
  110: '#5fb4fd',
  120: '#82c4fd',
  130: '#a3d3fe',
  140: '#c2e1fe',
  150: '#dcecff',
  160: '#f1f7ff',
};

/**
 * Ghi đè NỀN · CHỮ · VIỀN · BO GÓC · PHÔNG cho khớp `thietke.css`.
 *
 * 🔴 VÌ SAO PHẢI GHI ĐÈ THAY VÌ ĐỂ FLUENT TỰ LO: app có **hai hệ màu chạy song song** —
 *   Tailwind đọc `--nen-1`, Fluent đọc `colorNeutralBackground1`. Cùng một cái thẻ có thể có
 *   viền do Tailwind vẽ và nền do Fluent vẽ. Không ép chúng về một nguồn thì màn hình có hai
 *   sắc xám gần giống nhau, và mắt đọc ra "cái gì đó hơi bẩn" mà không chỉ được ra chỗ nào.
 *
 * ⛔ GIÁ TRỊ LẤY TỪ `var(--…)`, KHÔNG chép mã hex. Đây là điều làm phép phá của `GD-02` chạy
 *   được: đổi `--nen-1` của bộ tối thành đỏ thì **cả thẻ Fluent lẫn thẻ Tailwind** cùng đỏ, và
 *   bộ sáng không đổi. Chép hex vào đây là chỗ duy nhất sẽ đứng im — và nó sẽ tự tố cáo mình.
 * ⚠ `colorNeutralBackground1` nhận `rgb(var(--nen-1))` chứ không phải `var(--nen-1)`: biến chứa
 *   **ba số** (xem `thietke.css`), không phải một màu.
 */
function ghiDeToken(t: Theme): Theme {
  return {
    ...t,
    colorNeutralBackground1: 'rgb(var(--nen-1))',
    colorNeutralBackground2: 'rgb(var(--nen-2))',
    colorNeutralBackground3: 'rgb(var(--nen-2))',
    colorNeutralForeground1: 'rgb(var(--chu-1))',
    colorNeutralForeground2: 'rgb(var(--chu-2))',
    colorNeutralForeground3: 'rgb(var(--chu-3))',
    colorNeutralStroke1: 'rgb(var(--vien))',
    /*
     * ➕ `GD-07A` — hai token nữa bắc sang Fluent, để bảng màu mới không dừng ở Tailwind:
     *   `colorNeutralBackground1Hover` ô/hàng đang rê chuột → tầng "vật thể bấm được"
     *   `colorNeutralStroke2`          viền phụ → `--vien` (Fluent mặc định nhạt hơn hẳn)
     * ⚠ Thiếu hai dòng này thì đúng những chỗ Fluent tự tô (hàng `Tree` rê chuột, viền `Card`)
     *   giữ xám trung tính của nó giữa một trang xanh đêm — và chúng là thứ mắt đọc ra "bẩn"
     *   mà không chỉ được ra chỗ nào.
     */
    colorNeutralBackground1Hover: 'rgb(var(--nen-3))',
    colorNeutralStroke2: 'rgb(var(--vien))',
    /* Bo góc theo mẫu lee duyệt — cùng con số với `tailwind.config.js` (`the` 18 · `nut` 12). */
    borderRadiusXLarge: '18px',
    borderRadiusLarge: '12px',
    /* Một biến, hai hệ: Fluent và Tailwind cùng đọc `--font-chu`. */
    fontFamilyBase: 'var(--font-chu), "Segoe UI", system-ui, sans-serif',
  };
}

const themeToi = ghiDeToken(createDarkTheme(NHAN_SDKD));
const themeSang = ghiDeToken(createLightTheme(NHAN_SDKD));

/**
 * ⚠ MỘT LỚP RIÊNG chỉ để **đọc** chế độ rồi chọn theme: `useGiaoDien()` phải được gọi BÊN
 *   TRONG `GiaoDienProvider`, mà `FluentProvider` thì lại nằm bên trong nữa. Không tách ra thì
 *   không có chỗ nào hợp lệ để hỏi.
 */
function VoFluent({ children }: { children: React.ReactNode }) {
  const { cheDo } = useGiaoDien();
  return (
    <FluentProvider theme={cheDo === 'toi' ? themeToi : themeSang}>
      {children}
    </FluentProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          {/*
            * ═══════════════════════════════════════════════════════════════════════════
            * THỨ TỰ BỌC LÀ MỘT PHẦN CỦA YÊU CẦU, không phải chuyện lồng cho gọn:
            *
            *     AuthProvider  >  GiaoDienProvider  >  FluentProvider  >  ToastProvider
            *
            *   · `GiaoDienProvider` **ngoài** `FluentProvider` — vì `FluentProvider` phải HỎI
            *     nó đang ở chế độ nào để chọn theme. Ngược lại thì không có chỗ nào hỏi được.
            *   · `FluentProvider` **trong** `AuthProvider` — `Khoa.tsx` gọi `useNguoiCap()`
            *     (đọc `/me`) ngay bên trong một thành phần Fluent.
            *   · `FluentProvider` **ngoài** `ToastProvider` — thông báo dựng bằng thành phần
            *     Fluent được, mà không phải bọc thêm một `FluentProvider` thứ hai. Hai
            *     `FluentProvider` lồng nhau là hai gốc token, và đó là chỗ chủ đề trôi.
            *
            * 🔴 `theme` ở ĐÚNG MỘT CHỖ này là điều làm phép phá của `GD-02` chạy được: đổi
            *   `--nen-1` của bộ tối một lần thì cả trang đổi màu ở chế độ tối, và chế độ sáng
            *   KHÔNG đổi. Chỗ nào đứng im là chỗ còn một mã màu gõ tay.
            * ═══════════════════════════════════════════════════════════════════════════
            */}
          <GiaoDienProvider>
            <VoFluent>
              <ToastProvider>
                <App />
              </ToastProvider>
            </VoFluent>
          </GiaoDienProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
);

// PWA: chỉ đăng ký service worker ở bản build thật, tránh làm rối khi lập trình.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      /* không cài được thì app vẫn chạy bình thường trên trình duyệt */
    });
  });
}
